import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Search, Filter, Plus, Star, Mail, Phone, Calendar, Clock,
  FileText, Users, BarChart3, Tags, Settings, ChevronDown,
  MessageSquare, ArrowUpRight, CheckCircle, AlertCircle, Upload,
  Download, Link2, UserPlus
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AddContactModal from '../../components/modals/AddContactModal';
import ImportDataModal from '../../components/modals/ImportDataModal';
import ContactForm from '../../components/crm/ContactForm';
import toast from 'react-hot-toast';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  status: string;
  created_at: string;
  last_contact: string;
  value: number;
  tags: string[];
  relationship_strength: number;
}

const CRM: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'contacts' | 'activities' | 'pipeline' | 'reports'>('contacts');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);

  const queryClient = useQueryClient();

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Client[];
    }
  });

  const addContact = useMutation({
    mutationFn: async (contactData: any) => {
      const { data, error } = await supabase
        .from('clients')
        .insert([{
          first_name: contactData.firstName,
          last_name: contactData.lastName,
          email: contactData.email,
          phone: contactData.phone,
          status: 'active',
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Contact added successfully');
    },
    onError: (error) => {
      toast.error('Failed to add contact');
      console.error('Error adding contact:', error);
    }
  });

  const importContacts = useMutation({
    mutationFn: async (contacts: any[]) => {
      const { data, error } = await supabase
        .from('clients')
        .insert(
          contacts.map(contact => ({
            first_name: contact['First_Name'],
            last_name: contact['Last_Name'],
            email: contact['Email'],
            phone: contact['Phone'],
            status: contact['Client_Status'] || 'active',
            created_at: new Date().toISOString()
          }))
        );

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Contacts imported successfully');
    },
    onError: (error) => {
      toast.error('Failed to import contacts');
      console.error('Error importing contacts:', error);
    }
  });

  const handleSaveContact = async (contactData: any) => {
    await addContact.mutate(contactData);
  };

  const handleImportData = async (data: any[]) => {
    await importContacts.mutate(data);
  };

  const stats = [
    {
      label: 'Total Clients',
      value: clients?.length || 0,
      change: '+0%',
      icon: <Users className="h-5 w-5 text-primary" />
    },
    {
      label: 'Active Opportunities',
      value: '0',
      change: '+0%',
      icon: <Star className="h-5 w-5 text-warning" />
    },
    {
      label: 'Meetings This Week',
      value: '0',
      change: '0',
      icon: <Calendar className="h-5 w-5 text-success" />
    },
    {
      label: 'Tasks Due Today',
      value: '0',
      change: '0',
      icon: <CheckCircle className="h-5 w-5 text-error" />
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Client Relationship Management</h1>
            <p className="mt-2 text-gray-600">Manage your client relationships and track opportunities</p>
          </div>
          <div>
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="btn btn-primary flex items-center"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Client List
            </button>
          </div>
        </div>
      </div>

      {/* Relationship Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Link2 className="h-5 w-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-success">+0%</span>
          </div>
          <h3 className="text-2xl font-bold mt-4">0</h3>
          <p className="text-gray-600">Recent Connections</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <UserPlus className="h-5 w-5 text-secondary" />
            </div>
            <span className="text-sm font-medium text-success">+0%</span>
          </div>
          <h3 className="text-2xl font-bold mt-4">0%</h3>
          <p className="text-gray-600">Relationship Strength</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Users className="h-5 w-5 text-accent" />
            </div>
            <span className="text-sm font-medium text-success">+0%</span>
          </div>
          <h3 className="text-2xl font-bold mt-4">0</h3>
          <p className="text-gray-600">Active Relationships</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="p-2 bg-gray-50 rounded-lg">
                {stat.icon}
              </div>
              <span className={`text-sm font-medium ${
                stat.change.startsWith('+') ? 'text-success' : 'text-gray-500'
              }`}>
                {stat.change}
              </span>
            </div>
            <h3 className="text-2xl font-bold mt-4">{stat.value}</h3>
            <p className="text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>

      {showContactForm ? (
        <div className="mb-8">
          <ContactForm 
            onSuccess={() => setShowContactForm(false)}
            onCancel={() => setShowContactForm(false)}
          />
        </div>
      ) : (
        <>
          {/* Main Navigation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('contacts')}
                  className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                    activeTab === 'contacts'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Users className="h-5 w-5 mr-2" />
                  Contacts
                </button>
                <button
                  onClick={() => setActiveTab('activities')}
                  className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                    activeTab === 'activities'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Clock className="h-5 w-5 mr-2" />
                  Activities
                </button>
                <button
                  onClick={() => setActiveTab('pipeline')}
                  className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                    activeTab === 'pipeline'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Pipeline
                </button>
                <button
                  onClick={() => setActiveTab('reports')}
                  className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm ${
                    activeTab === 'reports'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FileText className="h-5 w-5 mr-2" />
                  Reports
                </button>
              </nav>
            </div>

            {/* Action Bar */}
            <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center space-x-4 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary appearance-none"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Contacts</option>
                    <option value="active">Active</option>
                    <option value="prospect">Prospect</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div className="w-full sm:w-auto">
                <button 
                  className="btn btn-primary w-full sm:w-auto"
                  onClick={() => setShowContactForm(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-4">
              {activeTab === 'contacts' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Value
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tags
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                            </div>
                            <p className="mt-2">Loading contacts...</p>
                          </td>
                        </tr>
                      ) : clients && clients.length > 0 ? (
                        clients.map((client) => (
                          <tr key={client.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                                  <span className="text-primary font-medium">
                                    {client.first_name[0]}{client.last_name[0]}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {client.first_name} {client.last_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {client.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                client.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : client.status === 'prospect'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {client.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              ${client.value?.toLocaleString() || '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {client.last_contact
                                ? format(new Date(client.last_contact), 'MMM d, yyyy')
                                : 'Never'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap gap-2">
                                {client.tags?.map((tag, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end space-x-3">
                                <button className="text-gray-400 hover:text-gray-500">
                                  <Mail className="h-5 w-5" />
                                </button>
                                <button className="text-gray-400 hover:text-gray-500">
                                  <Phone className="h-5 w-5" />
                                </button>
                                <button className="text-gray-400 hover:text-gray-500">
                                  <MessageSquare className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                            <div className="py-8">
                              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                              <p className="text-gray-500 mb-2">No contacts found</p>
                              <p className="text-sm text-gray-400 mb-4">
                                Add your first contact to get started
                              </p>
                              <button 
                                onClick={() => setShowContactForm(true)}
                                className="btn btn-primary mx-auto"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Contact
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'activities' && (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Activity tracking features coming soon</p>
                  <p className="text-sm text-gray-400">
                    Track client interactions, meetings, and follow-ups
                  </p>
                </div>
              )}

              {activeTab === 'pipeline' && (
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Pipeline management features coming soon</p>
                  <p className="text-sm text-gray-400">
                    Track opportunities and manage your sales pipeline
                  </p>
                </div>
              )}

              {activeTab === 'reports' && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">Reporting features coming soon</p>
                  <p className="text-sm text-gray-400">
                    Generate insights and analytics about your client relationships
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <AddContactModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveContact}
      />

      <ImportDataModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportData}
      />
    </div>
  );
};

export default CRM;